export type Role = 'owner' | 'gm' | 'ops' | 'chef' | 'marketing'
export type SubscriptionTier = 'starter' | 'growth' | 'pro'
export type RestaurantStatus = 'active' | 'suspended' | 'cancelled'

export interface Restaurant {
  id: string
  name: string
  legal_name: string | null
  address: string | null
  city: string | null
  cuisine_type: string | null
  logo_url: string | null
  brand_colour_primary: string
  brand_colour_secondary: string
  subscription_tier: SubscriptionTier
  status: RestaurantStatus
  billing_email: string | null
  stripe_customer_id: string | null
  measurement_system: 'metric' | 'imperial'
  // Up to 3 chars, admin-set — prefixes generated order numbers, e.g.
  // "AA" → AA-260821-01 (org code - YYMMDD - daily sequence).
  order_code_prefix: string | null
  // URL-safe storefront identifier — /store/:slug works on any domain today
  // (rezzops.pages.dev, a custom domain later, etc.) with no DNS dependency.
  slug: string | null
  custom_domain: string | null
  created_at: string
}

export interface AiIngredient {
  qty: string
  unit: string
  name: string
  prep: string | null
  ingredient_id?: string   // linked library ingredient ID
}

export interface AiPrepStep {
  timing: string
  task: string
}

export interface AiStep {
  n: number
  text: string
}

export interface AiPostProdStep {
  timing: string   // e.g. "After cooking", "After 30 min cool-down", "Before packing"
  task: string     // e.g. "Cool to below 40°C", "Pack in 250g containers, seal and label"
}

export interface AiRecipe {
  raw_notes?: string
  serves: string
  total_time_minutes: number | null  // total active cooking + passive waiting time (not overnight prep)
  ingredients: AiIngredient[]
  prep: AiPrepStep[]
  steps: AiStep[]
  post_production: AiPostProdStep[]  // cool-down, packing, labeling, storage
  notes: string | null
}

export interface AppUser {
  id: string
  restaurant_id: string
  role: Role
  full_name: string
  phone: string | null
  whatsapp_opt_in: boolean
  is_active: boolean
  last_seen_at: string | null
}

export interface NavItem {
  label: string
  href: string
  icon: string
  roles: Role[]
  tier?: SubscriptionTier
  badge?: number
}

export interface AlertSeverity {
  info: 'info'
  warning: 'warning'
  critical: 'critical'
}

export type OrderStatus = 'received' | 'in_prep' | 'ready' | 'dispatched' | 'completed' | 'cancelled'
export type POStatus = 'draft' | 'submitted' | 'approved' | 'rejected' | 'sent' | 'delivered' | 'cancelled'
export type CateringStatus = 'inquiry' | 'quoted' | 'deposit_paid' | 'confirmed' | 'in_production' | 'delivered' | 'completed' | 'cancelled'
// 'per_lb'/'boxed_per_lb' — weight-based, quantity means lbs ordered, not a
// count of the unit (same math either way: quantity × unit_price).
export type TraySize = 'half_tray' | 'medium_tray' | 'full_tray' | 'per_piece' | 'per_lb' | 'boxed_per_lb' | 'custom'

export interface CateringItemPricing {
  id: string
  restaurant_id: string
  menu_item_id: string
  half_tray_price: number | null
  half_tray_serves: number | null
  half_tray_pieces: number | null
  medium_tray_price: number | null
  medium_tray_serves: number | null
  medium_tray_pieces: number | null
  full_tray_price: number | null
  full_tray_serves: number | null
  full_tray_pieces: number | null
  per_piece_price: number | null
  min_pieces: number | null
  // Weight-based (sweets sold by the lb) — loose/tray vs. boxed are two
  // separate rates with their own minimum order, same product either way.
  per_lb_price: number | null
  per_lb_min: number | null
  boxed_per_lb_price: number | null
  boxed_per_lb_min: number | null
  notes: string | null
  updated_at: string
}

export interface CateringOrderItem {
  id: string
  catering_id: string
  menu_item_id: string | null
  name: string
  tray_size: TraySize | null
  quantity: number
  serves_per_unit: number | null
  unit: string
  unit_price: number
  is_complimentary: boolean
  notes: string | null
  sort_order: number
  // Meal-occasion tag (Breakfast/Lunch/Evening/Dinner), scoped to THIS
  // order's line item, not the product — the same dish can appear on one
  // order twice under two different occasions (e.g. Puri at breakfast and
  // at dinner). A fixed, standard set (see CATERING_SECTIONS) rather than a
  // freeform per-restaurant label — nothing shared to accidentally delete
  // out from under a different order.
  catering_section: CateringSectionKey | null
}

// Fixed, standard meal occasions — deliberately not a freeform/custom list.
// The order it's declared in is the canonical display order everywhere.
export const CATERING_SECTIONS = ['breakfast', 'lunch', 'evening', 'dinner'] as const
export type CateringSectionKey = typeof CATERING_SECTIONS[number]
export const CATERING_SECTION_LABELS: Record<CateringSectionKey, string> = {
  breakfast: 'Breakfast',
  lunch:     'Lunch',
  evening:   'Evening',
  dinner:    'Dinner',
}

export type FulfillmentType = 'delivery' | 'pickup'

export interface CateringOrder {
  id: string
  restaurant_id: string
  order_number: string | null
  status: CateringStatus
  client_name: string
  client_email: string | null
  client_phone: string
  client_company: string | null
  event_date: string
  event_time: string | null
  event_type: string | null
  guest_count: number | null
  venue_address: string | null
  venue_notes: string | null
  // Which of the standard meal occasions (see CATERING_SECTIONS) apply to
  // this event — chosen up front in Details, then those become the buckets
  // shown in Menu Items to add/move dishes into.
  event_sections: CateringSectionKey[]
  fulfillment_type: FulfillmentType | null
  delivery_fee: number | null
  // Delivery fee stays visible at full value everywhere (staff still knows
  // what delivery would normally cost) but is excluded from the total when
  // waived — the same "Comp" pattern used for individual order items,
  // applied to this one order-level fee instead of a line item.
  delivery_is_complimentary: boolean
  discount_amount: number | null
  discount_percentage: number | null
  quote_amount: number | null
  deposit_percentage: number | null
  deposit_amount: number | null
  deposit_paid_at: string | null
  balance_due: number | null
  balance_paid_at: string | null
  special_requests: string | null
  dietary_requirements: string | null
  notes: string | null
  internal_notes: string | null
  assigned_to: string | null
  has_customer_update: boolean
  source: string
  created_at: string
  updated_at: string
  items?: CateringOrderItem[]
}

export interface CateringOrderRevision {
  id: string
  restaurant_id: string
  catering_id: string
  changed_by: 'staff' | 'customer'
  changed_by_name: string | null
  quote_amount: number | null
  items: Pick<CateringOrderItem, 'name' | 'tray_size' | 'quantity' | 'unit_price' | 'notes'>[]
  notes: string | null
  created_at: string
}

// Web/storefront orders — deliberately separate from CateringOrder. Fixed
// price at checkout, no inquiry/quote negotiation, no deposit split — a
// genuinely different workflow, not just another "source" on the same
// status lifecycle.
export type StoreOrderStatus = 'placed' | 'confirmed' | 'ready' | 'completed' | 'cancelled'

export interface StoreOrderItem {
  id: string
  store_order_id: string
  menu_item_id: string | null
  name: string
  quantity: number
  unit_price: number
  notes: string | null
  sort_order: number
}

export interface StoreOrder {
  id: string
  restaurant_id: string
  order_number: string | null
  client_name: string
  client_phone: string
  client_email: string | null
  fulfillment_type: FulfillmentType | null
  requested_date: string | null
  venue_address: string | null
  notes: string | null
  internal_notes: string | null
  status: StoreOrderStatus
  subtotal: number | null
  tax_amount: number | null
  total_amount: number | null
  paid_at: string | null
  source: string
  created_at: string
  updated_at: string
  items?: StoreOrderItem[]
}
export type MenuItemStatus = 'active' | 'seasonal' | 'eighty_sixed' | 'retired'

export type SourceType = 'inhouse' | 'procured'

export interface MenuCategory {
  id: string
  restaurant_id: string
  name: string
  display_order: number
  is_active: boolean
}

export interface MenuItemCosting {
  batch_size: number                          // how many servings this recipe makes
  labor_hours_per_batch: number
  labor_cost_per_hour: number
  packaging_cost_per_serving: number
  delivery_cost_per_serving: number
  ingredient_costs: Record<string, number>    // ingredient name → cost per unit (manual overrides)
}

export interface MenuItem {
  id: string
  restaurant_id: string
  category_id: string | null
  name: string
  description: string | null
  price: number
  wholesale_price: number | null
  upc_code: string | null
  regional_name: string | null
  status: MenuItemStatus
  source_type: SourceType
  procured_supplier_id: string | null
  available_in_store: boolean
  available_catering: boolean
  available_store_supply: boolean
  // Distinct from available_in_store (the physical counter) — controls
  // whether this item shows on the public web storefront.
  available_online: boolean
  dietary_flags: string[]
  allergens: string[]
  sort_order: number
  is_deleted: boolean
  is_addon: boolean
  images: string[]
  ai_recipe: AiRecipe | null
  costing: MenuItemCosting | null
  created_at: string
}

export interface MenuItemAddon {
  id: string
  restaurant_id: string
  menu_item_id: string
  addon_id: string
  sort_order: number
}

export interface BuyerProduct {
  id: string
  restaurant_id: string
  buyer_id: string
  menu_item_id: string
  contract_price: number | null
  notes: string | null
  created_at: string
}

export type InvoiceStatus = 'draft' | 'sent' | 'partial' | 'paid' | 'overdue' | 'cancelled'

export interface BuyerContact {
  id: string
  buyer_id: string
  restaurant_id: string
  name: string
  role: string | null
  phone: string | null
  whatsapp: string | null
  email: string | null
  is_primary: boolean
  created_at: string
}

export interface Buyer {
  id: string
  restaurant_id: string
  name: string
  buyer_code: string | null
  city_code: string | null
  city: string | null
  contact_name: string | null
  phone: string | null
  email: string | null
  address: string | null
  is_active: boolean
  default_payment_terms: string
  created_at: string
  contacts?: BuyerContact[]
}

export type PaymentTerms = 'COD' | 'Due on Receipt' | 'Net 15' | 'Net 30' | 'Net 45' | 'Net 60' | 'Net EOM'

export interface Invoice {
  id: string
  restaurant_id: string
  buyer_id: string
  invoice_number: string
  status: InvoiceStatus
  issued_date: string
  due_date: string | null
  po_number: string | null
  payment_terms: PaymentTerms
  tax_percent: number
  discount_amount: number
  amount_paid: number
  payment_notes: string | null
  notes: string | null
  created_at: string
  buyer?: Buyer
  line_items?: InvoiceLineItem[]
}

export interface InvoiceLineItem {
  id: string
  invoice_id: string
  menu_item_id: string | null
  description: string
  upc_code: string | null
  regional_name: string | null
  qty: number
  unit_price: number
  total: number
}

export type StockTxnType = 'in' | 'out' | 'adjustment'
export type StockRefType = 'production' | 'invoice' | 'wastage' | 'adjustment'

export interface ProductStock {
  id: string
  restaurant_id: string
  menu_item_id: string
  on_hand: number
  unit: string
  updated_at: string
}

export interface ProductStockTxn {
  id: string
  restaurant_id: string
  menu_item_id: string
  txn_type: StockTxnType
  qty: number
  reference_type: StockRefType
  reference_id: string | null
  notes: string | null
  created_at: string
  created_by: string | null
}

export type ItemType = 'ingredient' | 'material'
export type MaterialCategory = 'packaging' | 'cleaning' | 'kitchen_supply' | 'other'

export interface Ingredient {
  id: string
  restaurant_id: string
  name: string
  brand: string | null
  unit: string
  cost_per_unit: number | null
  supplier_id: string | null
  is_perishable: boolean
  is_deleted: boolean
  item_type: ItemType
  material_category: MaterialCategory | null
}

export interface Supplier {
  id: string
  restaurant_id: string
  name: string
  contact_name: string | null
  phone: string | null
  email: string | null
  is_active: boolean
}

export interface SupplierCatalogItem {
  id: string
  restaurant_id: string
  supplier_id: string
  item_name: string
  unit: string | null
  unit_price: number | null
  is_procured: boolean
  notes: string | null
  created_at: string
}

export interface IngredientSupplier {
  id: string
  ingredient_id: string
  supplier_id: string
  is_preferred: boolean
  unit_price: number | null
  notes: string | null
  supplier?: Supplier
}

export interface RecipeIngredient {
  id: string
  recipe_id: string
  ingredient_id: string
  quantity_per_serving: number
  unit: string
  yield_percentage: number
  notes: string | null
  ingredient?: Ingredient
}

export interface Recipe {
  id: string
  menu_item_id: string
  version: number
  is_current: boolean
  batch_size: number
  notes: string | null
  created_at: string
  recipe_ingredients?: RecipeIngredient[]
}

export interface ProcedureStep {
  step: number
  description: string
  duration_minutes: number | null
  equipment: string | null
  quality_check: string | null
}

export interface Procedure {
  id: string
  menu_item_id: string
  version: number
  is_current: boolean
  steps: ProcedureStep[]
  created_at: string
}

export interface InventoryPerishable {
  id: string
  restaurant_id: string
  ingredient_id: string
  quantity: number
  unit: string
  par_level: number | null
  reorder_point: number | null
  expiry_date: string | null
  location: string | null
  counted_at: string | null
  notes: string | null
  ingredient?: Ingredient
}

export interface InventoryNonperishable {
  id: string
  restaurant_id: string
  ingredient_id: string
  quantity: number
  unit: string
  par_level: number | null
  reorder_point: number | null
  counted_at: string | null
  ingredient?: Ingredient
}

export interface PurchaseOrder {
  id: string
  restaurant_id: string
  supplier_id: string | null
  status: POStatus
  total_amount: number | null
  notes: string | null
  created_at: string
  submitted_at: string | null
  approved_at: string | null
  delivered_at: string | null
  items?: PurchaseOrderItem[]
  supplier?: Supplier
}

export interface PurchaseOrderItem {
  id: string
  po_id: string
  ingredient_id: string
  quantity_ordered: number
  quantity_received: number | null
  unit: string
  unit_price: number | null
  notes: string | null
  ingredient?: Ingredient
}
